/*
 * 戒导航 · 知识库审核（jiedh/wiki-review）—— 审核结果通知卡片
 *
 * 设计依据：doc/知识库内容审核设计方案.md §7.3「回执与通知」/ §9.1「作者侧体验」。
 *
 * 一句话职责：把「你提交的知识库文章审核通过了 / 被驳回了（附理由）」显示给作者本人。
 *
 * 为什么可以直接读通知里的数据、不需要再请求文章：
 *   后端 AbstractWikiReviewBlueprint::getData() 里放的是**渲染所需的全部快照**
 *   （标题 / 展示页链接 / 编辑页链接 / 驳回理由）。这一点是刻意的 ——
 *   被驳回的文章是草稿（对外不可见），任何"再查一次文章"的做法都可能失败。
 *
 * ⚠️ 安全：articleTitle / reason 都是**纯文本**（后端已做长度截断），
 *   这里只作为文本节点渲染，绝不可用 m.trust / dangerouslySetInnerHTML。
 *
 * ⚠️ 字段契约：下方 ReceiptContent 的字段名必须与
 *   JieDH\WikiReview\Notify\AbstractWikiReviewBlueprint::getData() 保持一致；
 *   后端改名时此处必须同步（后端文件里也有同款提醒）。
 *
 * ⚠️ JSX 写法约定（勿改成 import mithril）：
 *   本项目所有扩展都不 import mithril，JSX 由 flarum-webpack-config 的 babel
 *   以 `pragma: 'm'` 编译成对**全局标识符 `m`** 的调用（详见 packages/review 的同款说明）。
 *
 * 范本：packages/review/js/src/forum/components/ModerationReceiptNotification.tsx。
 */
import app from 'flarum/forum/app';
import Notification from 'flarum/forum/components/Notification';

/** 审核结论：与后端 WikiReviewReceipt::ACTION_* 常量一致。 */
const ACTION_APPROVED = 'approved';
const ACTION_REJECTED = 'rejected';

/**
 * 通知数据契约（= AbstractWikiReviewBlueprint::getData() 的返回值）。
 */

/**
 * 各审核结论的图标（通知条目**左侧**的类型图标，由核心 Notification 基类的 icon() 渲染）。
 *
 * ⚠️ 图标选取红线（沿用 packages/review 的实机事故结论）：
 *   本表是「通知类型」图标，与核心「标记已读」按钮（'fas fa-check'）、
 *   以及 packages/notification 的删除按钮（'fas fa-trash-alt'）**同屏出现**，
 *   故本表禁止使用这两个图标，避免同一条通知里出现两个一模一样的图标。
 */
const ICONS = {
  [ACTION_APPROVED]: 'fas fa-check-circle',
  [ACTION_REJECTED]: 'fas fa-exclamation-circle'
};
export default class WikiReviewNotification extends Notification {
  /**
   * 通知正文：按审核结论取对应文案。
   *
   * 文案键形如 jiedh-wiki-review.forum.notifications.approved_text，
   * 见 packages/wiki-review/resources/locale/{zh-Hans,en}.yml。
   */
  content() {
    const data = this.data();
    return app.translator.trans(`jiedh-wiki-review.forum.notifications.${data.action}_text`, {
      title: data.articleTitle
    });
  }
  icon() {
    return ICONS[this.data().action] || 'fas fa-book';
  }

  /**
   * 点击通知跳转的地址。
   *
   *   · 通过 ⇒ 跳到「文章展示页」（articleUrl），作者可直接查看已发布的文章；
   *   · 驳回 ⇒ 跳到「文章编辑页」（editUrl），让作者**一键去修改**（这是最有用的动作）。
   *
   * 两个链接都是后端在发送通知时算好的快照（见 AbstractWikiReviewBlueprint 构造函数），
   * 前端不再自行拼接 —— 避免 slug 变化或文章删除后链接失效。
   */
  href() {
    const {
      action,
      articleUrl,
      editUrl
    } = this.data();
    if (action === ACTION_REJECTED) {
      return editUrl || articleUrl || '';
    }
    return articleUrl || '';
  }

  /**
   * 副标题：驳回时显示「驳回理由」。
   *
   * 通过时不显示副标题（返回空字符串，通知条目会自动略过该行）。
   *
   * ⚠️ 必须用 div 包裹：父容器 .HeaderListItem-excerpt 是
   *   white-space: nowrap + overflow: hidden，靠内联元素换行无效。
   */
  excerpt() {
    const reason = this.data().reason;
    if (!reason) return '';
    return m("div", null, app.translator.trans('jiedh-wiki-review.forum.notifications.reason', {
      reason
    }));
  }

  /**
   * 读取通知携带的数据（防御：后端数据结构异常时不抛错、不白屏）。
   */
  data() {
    const content = this.attrs.notification.content() || {};
    return {
      action: String(content.action || ACTION_APPROVED),
      articleId: Number(content.articleId || 0),
      articleTitle: String(content.articleTitle || ''),
      articleUrl: content.articleUrl ? String(content.articleUrl) : '',
      editUrl: content.editUrl ? String(content.editUrl) : '',
      reason: content.reason ? String(content.reason) : null
    };
  }
}
flarum.reg.add('jiedh-wiki-review', 'forum/components/WikiReviewNotification', WikiReviewNotification);